import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for auth/me endpoint - let AuthContext handle it gracefully
      // This allows the landing page to display for unauthenticated users with stale tokens
      const requestUrl = error.config?.url || ''
      if (!requestUrl.includes('/auth/me')) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        const portalUrl = localStorage.getItem('sso_portal_login_url')
        window.location.href = portalUrl || '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Extract tenant slug from JWT for unauthenticated image URLs (<img src>).
// JWT payload is base64url-encoded JSON in the second segment.
export function getTenantParam(): string {
  try {
    const token = localStorage.getItem('token')
    if (!token) return ''
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.tenant ? `tenant=${payload.tenant}` : ''
  } catch {
    return ''
  }
}

// ---------------------------------------------------------------------------
// Media token — short-lived signed token appended (&mt=...) to <img src>
// image/thumbnail URLs, which cannot carry an Authorization header. Fetched
// after auth from /auth/media-token, cached, and refreshed proactively at
// 80% of its TTL. Components subscribe (via useMediaToken) so they re-render
// once the token arrives on initial load.
// ---------------------------------------------------------------------------
let mediaToken = ''
let mediaTokenParam = ''
let mediaTokenFetchedAt = 0
let mediaTokenTtlMs = 0
let mediaTokenPromise: Promise<void> | null = null
let mediaTokenRefreshTimer: ReturnType<typeof setTimeout> | null = null
const mediaTokenListeners = new Set<() => void>()

async function refreshMediaToken(): Promise<void> {
  if (!localStorage.getItem('token')) return
  try {
    const response = await api.get('/auth/media-token')
    mediaToken = response.data.token
    mediaTokenParam = mediaToken ? `mt=${encodeURIComponent(mediaToken)}` : ''
    mediaTokenTtlMs = (response.data.expires_in || 3600) * 1000
    mediaTokenFetchedAt = Date.now()
    if (mediaTokenRefreshTimer) clearTimeout(mediaTokenRefreshTimer)
    mediaTokenRefreshTimer = setTimeout(() => {
      ensureMediaToken()
    }, mediaTokenTtlMs * 0.8)
    mediaTokenListeners.forEach((listener) => listener())
  } catch {
    // Best-effort: retried on the next ensureMediaToken() call
  }
}

// Fetch/refresh the media token if missing or nearing expiry (>=80% of TTL).
// Deduplicates concurrent calls. Await this to guarantee a token before
// building URLs, or rely on the subscription for a re-render when it lands.
export function ensureMediaToken(): Promise<void> {
  const fresh = mediaToken && Date.now() - mediaTokenFetchedAt < mediaTokenTtlMs * 0.8
  if (fresh) return Promise.resolve()
  if (!mediaTokenPromise) {
    mediaTokenPromise = refreshMediaToken().finally(() => {
      mediaTokenPromise = null
    })
  }
  return mediaTokenPromise
}

// Returns 'mt=...' for <img src> URLs, or '' until the token has been
// fetched (subscribers are notified when it arrives).
export function getMediaTokenParam(): string {
  return mediaTokenParam
}

export function subscribeMediaToken(listener: () => void): () => void {
  mediaTokenListeners.add(listener)
  ensureMediaToken() // kick off the initial fetch when a component mounts
  return () => {
    mediaTokenListeners.delete(listener)
  }
}

// Auth types
export interface User {
  id: number
  username: string
  email: string
  full_name: string | null
  is_active: boolean
  is_admin: boolean
  user_type: string
  created_at: string
  last_login: string | null
}

export interface LoginResponse {
  access_token: string
  token_type: string
}

// Auth functions
export async function login(username: string, password: string): Promise<LoginResponse> {
  const formData = new URLSearchParams()
  formData.append('username', username)
  formData.append('password', password)

  const response = await api.post('/auth/login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
  return response.data
}

export interface SSOConfig {
  enabled: boolean
  login_only: boolean
  portal_login_url: string | null
}

export async function getSSOConfig(): Promise<SSOConfig> {
  const response = await api.get('/auth/sso-config')
  return response.data
}

export async function getCurrentUser(): Promise<User> {
  const response = await api.get('/auth/me')
  return response.data
}

export async function updateCurrentUser(data: {
  email?: string
  full_name?: string
  password?: string
}): Promise<User> {
  const response = await api.put('/auth/me', data)
  return response.data
}

// User management (admin)
export async function listUsers(): Promise<User[]> {
  const response = await api.get('/users/')
  return response.data
}

export async function getUser(userId: number): Promise<User> {
  const response = await api.get(`/users/${userId}`)
  return response.data
}

export async function createUser(data: {
  username: string
  email: string
  password: string
  full_name?: string
  is_admin?: boolean
  user_type?: string
}): Promise<User> {
  const response = await api.post('/users/', data)
  return response.data
}

export async function updateUser(
  userId: number,
  data: {
    email?: string
    full_name?: string
    password?: string
    is_active?: boolean
    is_admin?: boolean
    user_type?: string
  }
): Promise<User> {
  const response = await api.put(`/users/${userId}`, data)
  return response.data
}

export async function deleteUser(userId: number): Promise<void> {
  await api.delete(`/users/${userId}`)
}

// Types
/** Per-part sheet-nesting inputs & overrides (print_jobs.nesting_config). */
export interface NestingConfig {
  enabled?: boolean
  unfolded_length?: number
  unfolded_width?: number
  offset_length?: number
  offset_width?: number
  add_blanks?: number
  /** Legacy flag — `rounding` wins when set */
  round_up_to_sheet?: boolean
  /** How charged sheets round: exact = blank area; fractions round sheets up */
  rounding?: 'exact' | 'quarter' | 'third' | 'half' | 'full'
  sheet_cost_override?: number
  sheet_length_override?: number
  sheet_width_override?: number
  cost_per_blank_add?: number
  total_cost_add?: number
  volume_discount_percent?: number
}

/** Nesting rollup returned inside CostBreakdown when nesting is enabled. */
export interface NestingCostResult {
  blank_length?: number
  blank_width?: number
  total_blanks?: number
  blanks_per_sheet?: number
  sheets_required?: number
  rounding?: 'exact' | 'quarter' | 'third' | 'half' | 'full'
  sheet_cost?: number
  nested_total_cost?: number
  material_cost_per_part?: number
  volume_discount_applied?: number
  sheet_length?: number
  sheet_width?: number
  edge_boundary?: number
  part_gap?: number
  sheet_cost_source?: 'override' | 'weight_based'
  unfolded_source?: 'config' | 'geometry'
  error?: string
}

/** One extra material line beyond the primary sheet (Uptool "Materials & Parts"). */
export interface ExtraMaterialLine {
  name: string
  material_id?: number | null
  thickness_inches?: number | null
  cost_per_lb?: number | null
  unfolded_length?: number | null
  unfolded_width?: number | null
  quantity_per_part?: number | null
  /** When set (0 is valid), wins over the computed weight-based cost */
  direct_cost_per_part?: number | null
}

/** One purchased/hardware line rolled into the part price before markup. */
export interface PurchasedPartLine {
  name: string
  cost_per_unit?: number | null
  units_per_part?: number | null
}

/** Per-part material modifications (print_jobs.material_extras). */
export interface MaterialExtras {
  thickness_override?: number | null
  cost_per_lb_override?: number | null
  extra_lines?: ExtraMaterialLine[]
  purchased_parts?: PurchasedPartLine[]
}

export interface PrintJob {
  id: number
  filename: string
  status: string
  uploaded_at: string
  processed_at: string | null
  error_message: string | null
  converted_path: string | null
  converted_pages: string[] | null
  page_count: number
  customer_id: number | null
  is_weldment: boolean
  assembly_id: number | null
  // Material selection
  selected_material_id: number | null
  // Powder coat color selection (null = auto-pick from finish)
  selected_powder_coat_id: number | null
  // Cost override fields
  override_material_cost: number | null
  override_laser_cost: number | null
  process_cost_overrides: Record<string, number> | null
  custom_markup_percent: number | null
  costs_locked: boolean
  // Sheet-nesting overrides + per-part notes (Uptool-style estimator)
  nesting_config?: NestingConfig | null
  external_notes?: string | null
  internal_notes?: string | null
  material_extras?: MaterialExtras | null
  // JobBoss sync fields
  jobboss_part_number: string | null
  jobboss_synced_at: string | null
  jobboss_sync_error: string | null
  jobboss_status: string | null
  jobboss_last_checked: string | null
  jobboss_order_number: string | null
  jobboss_push_log: Array<{ timestamp: string; part_number: string; success: boolean; error: string | null }> | null
  // Approval workflow
  approval_stage_id: number | null
  approval_history: Array<{ stage: string; action: string; user: string; timestamp: string; notes?: string }> | null
  // Accuracy
  accuracy_score: number | null
  is_holdout: boolean
  // Extracted part number (list endpoint only)
  part_number?: string | null
  // Starred (protected from auto-cleanup)
  starred: boolean
}

export interface Process {
  id: number
  code: string
  name: string
  category: string | null
  unit: string
  description: string | null
  active: boolean
  jobboss_code: string | null
}

export interface Formula {
  id: number
  process_id: number
  name: string | null
  base_rate: number | null
  rate_per_hour: number | null
  minutes_per_unit: number | null
  formula_expression: string | null
  material_factors: Record<string, number> | null
  thickness_factors: Record<string, number> | null
  size_factors: Record<string, number> | null
  setup_cost: number | null
  minimum_charge: number | null
  notes: string | null
  effective_date: string | null
  active: boolean
}

export interface ExtractionResult {
  title_block: {
    part_number: string | null
    revision: string | null
    title: string | null
    material: string | null
    gauge: number | null
    thickness_inches: number | null
    weight_lbs: number | null
    finish: string | null
    customer: string | null
    drawn_by: string | null
    date: string | null
    scale: string | null
    sheet: string | null
  }
  geometry: {
    flat_length: number
    flat_width: number
    flat_area_sq_in: number
    perimeter_inches: number
    formed_dimensions: {
      length: number | null
      width: number | null
      height: number | null
    }
    num_bends?: number
    bend_allowance_inches?: number
    volume_cubic_in?: number
    surface_area_sq_in?: number
    is_sheet_metal?: boolean
  }
  processes: Array<{
    process_code: string
    description: string
    quantity: number
    unit: string
    parameters: Record<string, unknown>
    confidence: number
  }>
  notes: string[]
  flags: string[]
}

// API functions
export async function uploadPrint(file: File, rfqId?: number): Promise<PrintJob> {
  const formData = new FormData()
  formData.append('file', file)
  // Optional: attach the uploaded print straight onto an RFQ as a line item
  if (rfqId != null) formData.append('rfq_id', String(rfqId))

  const response = await api.post('/prints/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export async function getPrintJob(jobId: number): Promise<PrintJob> {
  const response = await api.get(`/prints/${jobId}`)
  return response.data
}

export interface PaginatedPrintJobs {
  items: PrintJob[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ListPrintJobsParams {
  page?: number
  page_size?: number
  status?: string
  search?: string
  requires_review?: boolean
  sort_by?: 'uploaded_at' | 'filename' | 'status'
  sort_order?: 'asc' | 'desc'
}

export interface QueueStats {
  awaiting_review: number
  low_confidence: number
  total_approved: number
}

export async function getQueueStats(): Promise<QueueStats> {
  const response = await api.get('/prints/queue-stats')
  return response.data
}

export async function listPrintJobs(params: ListPrintJobsParams = {}): Promise<PaginatedPrintJobs> {
  const response = await api.get('/prints/', { params })
  return response.data
}

export async function analyzePrint(jobId: number): Promise<void> {
  await api.post(`/prints/${jobId}/analyze`)
}

export async function attachDXF(jobId: number, file: File): Promise<{ message: string; geometry: { cut_length_inches: number; hole_count: number; bounding_box: Record<string, number>; has_contour: boolean } }> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post(`/prints/${jobId}/attach-dxf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

export async function overrideArea(jobId: number, areaSqIn: number): Promise<{ message: string; area_sq_in: number }> {
  const response = await api.post(`/prints/${jobId}/override-area`, null, { params: { area_sq_in: areaSqIn } })
  return response.data
}

export async function getJobStatuses(ids: number[]): Promise<{ jobs: { id: number; status: string; error_message: string | null }[] }> {
  const params = new URLSearchParams()
  ids.forEach(id => params.append('ids', String(id)))
  const response = await api.get(`/prints/status-batch?${params.toString()}`)
  return response.data
}

export type NavScope = 'parts' | 'weldments' | 'all'

export async function getNextPrintJob(
  jobId: number,
  opts?: { scope?: NavScope; unapprovedOnly?: boolean }
): Promise<{ next_id: number | null }> {
  const params: Record<string, string> = {}
  if (opts?.scope) params.scope = opts.scope
  if (opts?.unapprovedOnly) params.unapproved_only = 'true'
  const response = await api.get(`/prints/${jobId}/next`, { params })
  return response.data
}

export async function getPreviousPrintJob(
  jobId: number,
  opts?: { scope?: NavScope; unapprovedOnly?: boolean }
): Promise<{ previous_id: number | null }> {
  const params: Record<string, string> = {}
  if (opts?.scope) params.scope = opts.scope
  if (opts?.unapprovedOnly) params.unapproved_only = 'true'
  const response = await api.get(`/prints/${jobId}/previous`, { params })
  return response.data
}

export async function deletePrintJob(jobId: number): Promise<void> {
  await api.delete(`/prints/${jobId}`)
}

export interface Extraction {
  id: number
  print_job_id: number
  parsed_data: ExtractionResult | null
  /** Pristine AI output — compared against parsed_data to mark user-overridden fields */
  original_parsed_data?: ExtractionResult | null
  confidence_score: number | null
  requires_review: boolean
  reviewed: boolean
  field_confidence: Record<string, number> | null
  accuracy_score: number | null
  accuracy_breakdown: Record<string, number> | null
  is_ground_truth: boolean
  auto_corrections_captured: number | null
}

export interface PrintJobWithExtraction extends PrintJob {
  converted_pages: string[] | null
  extraction: Extraction | null
}

export async function getPrintJobFull(jobId: number): Promise<PrintJobWithExtraction> {
  const response = await api.get(`/prints/${jobId}/full`)
  return response.data
}

export async function getExtraction(jobId: number): Promise<Extraction> {
  const response = await api.get(`/prints/${jobId}/extraction`)
  return response.data
}

export async function updateExtraction(jobId: number, parsedData: ExtractionResult): Promise<Extraction> {
  const response = await api.put(`/prints/${jobId}/extraction`, { parsed_data: parsedData })
  return response.data
}

export async function approveExtraction(jobId: number): Promise<void> {
  await api.post(`/prints/${jobId}/approve`)
}

export interface CostOverrideRequest {
  material_cost?: number | null
  laser_cost?: number | null
  process_overrides?: Record<string, number> | null
  custom_markup_percent?: number | null
  lock_costs: boolean
}

export interface CostOverrideResponse {
  override_material_cost: number | null
  override_laser_cost: number | null
  process_cost_overrides: Record<string, number> | null
  custom_markup_percent: number | null
  costs_locked: boolean
}

export async function updateCostOverrides(
  jobId: number,
  overrides: CostOverrideRequest
): Promise<CostOverrideResponse> {
  const response = await api.put(`/prints/${jobId}/cost-overrides`, overrides)
  return response.data
}

export async function updateNestingConfig(
  jobId: number,
  config: NestingConfig
): Promise<{ job_id: number; nesting_config: NestingConfig | null }> {
  const response = await api.put(`/prints/${jobId}/nesting-config`, config)
  return response.data
}

// === Field provenance highlights (viewer overlays) ===

export interface FieldLocationBox {
  page: number
  /** Normalized 0..1 of the page image */
  x: number
  y: number
  w: number
  h: number
}

export interface FieldLocations {
  fields: Record<string, { value: string | null; boxes: FieldLocationBox[] }>
  pages: number
}

/** OCR-locate the extracted part number/material/finish + tightest ±tolerance on the print. */
export async function getFieldLocations(jobId: number): Promise<FieldLocations> {
  const response = await api.get(`/prints/${jobId}/field-locations`)
  return response.data
}

export async function updateMaterialExtras(
  jobId: number,
  extras: MaterialExtras
): Promise<{ job_id: number; material_extras: MaterialExtras | null }> {
  const response = await api.put(`/prints/${jobId}/material-extras`, extras)
  return response.data
}

export async function updatePartNotes(
  jobId: number,
  notes: { external_notes?: string | null; internal_notes?: string | null }
): Promise<{ job_id: number; external_notes: string | null; internal_notes: string | null }> {
  const response = await api.put(`/prints/${jobId}/part-notes`, notes)
  return response.data
}

export async function listProcesses(activeOnly: boolean = true): Promise<Process[]> {
  const response = await api.get('/processes/', { params: { active_only: activeOnly } })
  return response.data
}

export async function createProcess(process: {
  code: string
  name: string
  category?: string
  unit: string
  description?: string
}): Promise<Process> {
  const response = await api.post('/processes/', process)
  return response.data
}

export async function updateProcess(
  processId: number,
  data: {
    code?: string
    name?: string
    category?: string
    unit?: string
    description?: string
    active?: boolean
  }
): Promise<Process> {
  const response = await api.put(`/processes/${processId}`, data)
  return response.data
}

export async function deleteProcess(processId: number): Promise<void> {
  await api.delete(`/processes/${processId}`)
}

export interface JobBossMapping {
  id: number
  code: string
  name: string
  jobboss_code: string | null
  mapped: boolean
}

export async function getJobBossMappings(): Promise<JobBossMapping[]> {
  const response = await api.get('/processes/jobboss-mapping')
  return response.data
}

export async function saveJobBossMappings(
  mappings: { process_id: number; jobboss_code: string }[]
): Promise<Process[]> {
  const response = await api.post('/processes/jobboss-mapping', mappings)
  return response.data
}

export async function listFormulas(): Promise<Formula[]> {
  const response = await api.get('/formulas/')
  return response.data
}

export async function createFormula(formula: Omit<Formula, 'id' | 'active'>): Promise<Formula> {
  const response = await api.post('/formulas/', formula)
  return response.data
}

export async function updateFormula(id: number, formula: Partial<Formula>): Promise<Formula> {
  const response = await api.put(`/formulas/${id}`, formula)
  return response.data
}

export async function getEstimate(extractionId: number) {
  const response = await api.get(`/estimates/${extractionId}`)
  return response.data
}

export function getExportCSVUrl(extractionId: number): string {
  return `${API_URL}/exports/${extractionId}/csv`
}

export function getExportPDFUrl(extractionId: number): string {
  return `${API_URL}/exports/${extractionId}/pdf`
}

// Material types
export interface Material {
  id: number
  material_type: string
  gauge: string
  thickness_inches: number
  density_lb_per_cu_in: number
  cost_per_lb: number
  laser_speed_ipm: number
  weight_per_sq_in: number
  active: boolean
  created_at: string | null
}

export interface ShopSetting {
  id: number
  key: string
  value: string
  description: string | null
}

export interface CostBreakdown {
  material: {
    id: number
    type: string
    gauge: string
    thickness_inches: number
    cost_per_lb: number
    laser_speed_ipm: number
    /** Effective values reflect per-part overrides when these flags are set */
    thickness_overridden?: boolean
    cost_per_lb_overridden?: boolean
  } | null
  material_cost: number
  laser_cost: number
  laser_time_minutes: number
  pierce_count?: number
  cut_time_minutes?: number
  pierce_time_minutes?: number
  subtotal: number
  markup_percent: number
  markup_amount: number
  total_cost: number
  weight_lbs: number
  area_sq_in: number
  laser_inches: number
  has_laser_process: boolean
  is_structural?: boolean  // True for structural materials (channel, angle, tube, etc.)
  structural_length?: number  // Length in inches for structural parts
  errors: string[]
  warnings: string[]
  // Fields for manual cost entry when material not found
  extracted_material_description?: string
  material_not_found?: boolean
  manual_material_cost?: number | null  // Persisted manual material cost from database
  geometry_source?: 'dxf' | 'ai'  // Source of geometry data: DXF parsing or AI estimation
  material_cost_basis?: 'area' | 'nested'  // 'nested' when a nesting_config override drove material cost
  nesting?: NestingCostResult | null
  // Material modifications (present only when material_extras is set on the job)
  extra_materials_cost?: number
  extra_materials?: Array<ExtraMaterialLine & { cost_per_part?: number; priced_basis?: 'direct' | 'computed' | 'unpriced' }>
  purchased_parts_cost?: number
  purchased_parts?: Array<PurchasedPartLine & { cost_per_part?: number }>
  breakdown: {
    material: {
      area_sq_in: number
      weight_per_sq_in: number
      weight_lbs: number
      cost_per_lb: number
      total: number
    }
    laser: {
      inches: number
      speed_ipm: number
      pierce_count?: number
      cut_time_minutes?: number
      pierce_time_minutes?: number
      time_minutes: number
      rate_per_hour: number
      total: number
    }
  }
  // Auto-detected customer from title block
  customer_match?: {
    extracted_customer: string
    matched_customer_id: number
    matched_customer_name: string
    matched_customer_code: string
    match_score: number
    match_source: string
  }
  customer_id?: number
  customer_name?: string
  // Process costs with customer-specific formulas
  process_costs?: Array<{
    process_code: string
    process_name: string
    quantity: number
    unit: string
    base_rate: number
    setup_cost: number
    unit_cost: number
    total_cost: number
    is_custom_rate: boolean
    formula_source: string
  }>
}

// Material API functions
export async function listMaterials(materialType?: string, activeOnly: boolean = true): Promise<Material[]> {
  const params: Record<string, string | boolean> = { active_only: activeOnly }
  if (materialType) params.material_type = materialType
  const response = await api.get('/materials/', { params })
  return response.data
}

export async function getMaterial(materialId: number): Promise<Material> {
  const response = await api.get(`/materials/${materialId}`)
  return response.data
}

export async function createMaterial(material: {
  material_type: string
  gauge: string
  thickness_inches: number
  density_lb_per_cu_in: number
  cost_per_lb: number
  laser_speed_ipm: number
  active?: boolean
}): Promise<Material> {
  const response = await api.post('/materials/', material)
  return response.data
}

export async function updateMaterial(
  materialId: number,
  data: Partial<{
    material_type: string
    gauge: string
    thickness_inches: number
    density_lb_per_cu_in: number
    cost_per_lb: number
    laser_speed_ipm: number
    active: boolean
  }>
): Promise<Material> {
  const response = await api.put(`/materials/${materialId}`, data)
  return response.data
}

export async function deleteMaterial(materialId: number): Promise<void> {
  await api.delete(`/materials/${materialId}`)
}

export async function getMaterialTypes(): Promise<string[]> {
  const response = await api.get('/materials/types')
  return response.data
}

// Shop settings
export async function listShopSettings(): Promise<ShopSetting[]> {
  const response = await api.get('/materials/settings/all')
  return response.data
}

export async function getShopSetting(key: string): Promise<ShopSetting> {
  const response = await api.get(`/materials/settings/${key}`)
  return response.data
}

export async function updateShopSetting(key: string, value: string): Promise<ShopSetting> {
  const response = await api.put(`/materials/settings/${key}`, { value })
  return response.data
}

// Cost estimation
export async function getPrintJobCost(
  jobId: number,
  materialId?: number,
  orderQuantity?: number
): Promise<CostBreakdown> {
  const params: Record<string, number> = {}
  if (materialId) params.material_id = materialId
  // Sheets required (and thus nested material cost) depend on order quantity
  if (orderQuantity && orderQuantity > 1) params.order_quantity = orderQuantity
  const response = await api.get(`/estimates/print/${jobId}/cost`, { params })
  return response.data
}

// Manual material cost
export async function setManualMaterialCost(
  jobId: number,
  manualMaterialCost: number | null
): Promise<{ job_id: number; manual_material_cost: number | null }> {
  const response = await api.put(`/prints/${jobId}/manual-material-cost`, {
    manual_material_cost: manualMaterialCost
  })
  return response.data
}

export async function setStructuralOverride(
  jobId: number,
  isStructural: boolean | null
): Promise<{ job_id: number; is_structural_override: boolean | null }> {
  const response = await api.put(`/prints/${jobId}/structural-override`, {
    is_structural: isStructural
  })
  return response.data
}

export async function getManualMaterialCost(
  jobId: number
): Promise<{ job_id: number; manual_material_cost: number | null }> {
  const response = await api.get(`/prints/${jobId}/manual-material-cost`)
  return response.data
}

// Selected material persistence
export async function setSelectedMaterial(
  jobId: number,
  materialId: number | null
): Promise<{ job_id: number; selected_material_id: number | null }> {
  const response = await api.put(`/prints/${jobId}/selected-material`, {
    material_id: materialId
  })
  return response.data
}

// === Powder Coat Colors ===

export interface PowderCoatColor {
  id: number
  jobboss_part_number: string
  description: string | null
  vendor: string | null
  cost_per_sqi: number
  uses: number
  active: boolean
}

export interface PowderCoatMatch {
  job_id: number
  finish: string | null
  auto_match: Pick<PowderCoatColor, 'id' | 'jobboss_part_number' | 'description' | 'vendor' | 'cost_per_sqi'> | null
  selected_powder_coat_id: number | null
}

export async function listPowderCoatColors(): Promise<PowderCoatColor[]> {
  const response = await api.get('/materials/powder-coat-colors')
  return response.data
}

export async function getPowderCoatMatch(jobId: number): Promise<PowderCoatMatch> {
  const response = await api.get(`/prints/${jobId}/powder-coat-match`)
  return response.data
}

export async function setSelectedPowderCoat(
  jobId: number,
  powderCoatId: number | null
): Promise<{ job_id: number; selected_powder_coat_id: number | null }> {
  const response = await api.put(`/prints/${jobId}/selected-powder-coat`, {
    powder_coat_id: powderCoatId
  })
  return response.data
}

// === Assembly Types and API ===

export interface AssemblyPart {
  id: number
  print_job_id: number
  filename: string | null
  part_number: string | null
  quantity: number
  position_ref: string | null
  unit_cost: number
  line_cost: number
  status: string | null
  converted_path: string | null
  child_assembly_id: number | null
  child_assembly_name: string | null
  child_assembly_print_job_id: number | null
  excluded_from_sync: boolean
}

export interface Assembly {
  id: number
  name: string
  description: string | null
  print_job_id: number | null
  weldment_filename: string | null
  weldment_converted_path: string | null
  status: string
  weldment_status: string | null
  weldment_uploaded_at: string | null
  weldment_accuracy_score: number | null
  own_process_cost: number
  sub_parts_cost: number
  total_cost: number
  source: string | null
  parent_assembly_id: number | null
  parts: AssemblyPart[]
  created_at: string
  updated_at: string | null
}

export interface AssemblyCostBreakdown {
  assembly_id: number
  name: string
  // Weldment processes only (no material/laser - sub-parts ARE the material)
  weldment_processes: Array<{
    process_code: string
    process_name: string
    quantity: number
    unit: string
    cost: number
  }>
  weldment_process_cost: number
  weldment_subtotal: number
  // Sub-parts at cost (no individual margin)
  sub_parts: Array<{
    id: number
    print_job_id: number
    quantity: number
    position_ref: string | null
    filename: string
    part_number: string | null
    unit_cost: number
    line_cost: number
    status: string
  }>
  sub_parts_total: number
  // Added material lines (stored on the weldment print's material_extras)
  extra_materials?: Array<ExtraMaterialLine & { cost_per_part?: number; priced_basis?: string }>
  extra_materials_cost?: number
  purchased_parts?: Array<PurchasedPartLine & { cost_per_part?: number }>
  purchased_parts_cost?: number
  // Totals (margin applied only at assembly level)
  subtotal: number
  markup_percent: number
  markup_amount: number
  grand_total: number
  errors: string[]
  warnings: string[]
}

export interface BatchUploadResponse {
  batch_id: string
  total_files: number
  weldment_job_id: number | null
  weldment_filename: string | null
  sub_part_jobs: Array<{ job_id: number; filename: string }>
  message: string
}

export interface BatchStatus {
  batch_id: string
  total_files: number
  weldment: {
    job_id: number
    filename: string
    status: string
    has_extraction: boolean
    error_message: string | null
    part_number?: string
  } | null
  sub_parts: Array<{
    job_id: number
    filename: string
    status: string
    has_extraction: boolean
    error_message: string | null
    part_number?: string
  }>
  all_converted: boolean
  all_extracted: boolean
}

// Batch upload functions
export async function uploadBatch(
  files: File[],
  weldmentFilename?: string
): Promise<BatchUploadResponse> {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const params = weldmentFilename ? { weldment_filename: weldmentFilename } : {}

  const response = await api.post('/prints/upload-batch', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params,
  })
  return response.data
}

export async function getBatchStatus(batchId: string): Promise<BatchStatus> {
  const response = await api.get(`/prints/batch/${batchId}`)
  return response.data
}

export async function analyzeBatch(batchId: string): Promise<{ message: string; jobs_queued: Array<{ job_id: number; filename: string }> }> {
  const response = await api.post(`/prints/batch/${batchId}/analyze-all`)
  return response.data
}

export async function setBatchWeldment(batchId: string, jobId: number): Promise<{ message: string }> {
  const response = await api.post(`/prints/batch/${batchId}/set-weldment/${jobId}`)
  return response.data
}

export async function createAssemblyFromBatch(
  batchId: string,
  name: string,
  description?: string
): Promise<{ assembly_id: number; name: string; weldment: { job_id: number; filename: string } | null; sub_parts: Array<{ job_id: number; filename: string }> }> {
  const params: Record<string, string> = { name }
  if (description) params.description = description
  const response = await api.post(`/prints/batch/${batchId}/create-assembly`, null, { params })
  return response.data
}

// Assembly CRUD functions
export interface PaginatedAssemblies {
  items: Assembly[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ListAssembliesParams {
  page?: number
  page_size?: number
  status?: string
  search?: string
  sort_by?: 'name' | 'status' | 'created_at' | 'uploaded_at' | 'total_cost'
  sort_order?: 'asc' | 'desc'
}

export async function listAssemblies(params: ListAssembliesParams = {}): Promise<PaginatedAssemblies> {
  const response = await api.get('/assemblies/', { params })
  return response.data
}

export async function getAssembly(assemblyId: number): Promise<Assembly> {
  const response = await api.get(`/assemblies/${assemblyId}`)
  return response.data
}

export async function getAssemblyByPrintJobId(printJobId: number): Promise<Assembly | null> {
  try {
    const response = await api.get(`/assemblies/?weldment_job_id=${printJobId}`)
    const data = response.data
    const items = data.items || data
    return items.length > 0 ? items[0] : null
  } catch {
    return null
  }
}

export async function createAssembly(data: {
  name: string
  description?: string
  weldment_print_job_id?: number
  parts?: Array<{ print_job_id: number; quantity?: number; position_ref?: string }>
}): Promise<Assembly> {
  const response = await api.post('/assemblies/', data)
  return response.data
}

export async function updateAssembly(
  assemblyId: number,
  data: {
    name: string
    description?: string
    weldment_print_job_id?: number
    parts?: Array<{ print_job_id: number; quantity?: number; position_ref?: string }>
  }
): Promise<Assembly> {
  const response = await api.put(`/assemblies/${assemblyId}`, data)
  return response.data
}

export async function deleteAssembly(assemblyId: number): Promise<void> {
  await api.delete(`/assemblies/${assemblyId}`)
}

export async function addAssemblyPart(
  assemblyId: number,
  data: { print_job_id: number; quantity?: number; position_ref?: string }
): Promise<AssemblyPart> {
  const response = await api.post(`/assemblies/${assemblyId}/parts`, data)
  return response.data
}

export async function updateAssemblyPart(
  assemblyId: number,
  partId: number,
  quantity: number,
  positionRef?: string,
  excludedFromSync?: boolean
): Promise<void> {
  const params: Record<string, string | number | boolean> = { quantity }
  if (positionRef !== undefined) params.position_ref = positionRef
  if (excludedFromSync !== undefined) params.excluded_from_sync = excludedFromSync
  await api.put(`/assemblies/${assemblyId}/parts/${partId}`, null, { params })
}

export async function removeAssemblyPart(assemblyId: number, partId: number): Promise<void> {
  await api.delete(`/assemblies/${assemblyId}/parts/${partId}`)
}

export async function flattenAssembly(assemblyId: number): Promise<{
  message: string
  parts_added: number
  parts_updated: number
}> {
  const response = await api.post(`/assemblies/${assemblyId}/flatten`)
  return response.data
}

export async function getAssemblyCost(assemblyId: number): Promise<AssemblyCostBreakdown> {
  const response = await api.get(`/assemblies/${assemblyId}/cost`)
  return response.data
}

export async function recalculateAssemblyCost(assemblyId: number): Promise<{
  own_process_cost: number
  sub_parts_cost: number
  total_cost: number
}> {
  const response = await api.post(`/assemblies/${assemblyId}/recalculate`)
  return response.data
}

// Correction types
export interface Correction {
  id: number
  print_job_id: number
  field_path: string
  category: string
  original_value: string | null
  corrected_value: string
  context: string | null
  explanation: string | null
  auto_detected: boolean
  created_at: string
}

export interface CorrectionCreate {
  field_path: string
  category: string
  original_value?: string | null
  corrected_value: string
  context?: string | null
  explanation?: string | null
}

export interface CorrectionStats {
  total_corrections: number
  by_category: Record<string, number>
}

// Correction API functions
export async function createCorrection(
  printJobId: number,
  correction: CorrectionCreate
): Promise<Correction> {
  const response = await api.post(`/corrections/prints/${printJobId}`, correction)
  return response.data
}

export async function getPrintCorrections(printJobId: number): Promise<Correction[]> {
  const response = await api.get(`/corrections/prints/${printJobId}`)
  return response.data
}

export async function deleteCorrection(correctionId: number): Promise<void> {
  await api.delete(`/corrections/${correctionId}`)
}

export async function getCorrectionStats(): Promise<CorrectionStats> {
  const response = await api.get('/corrections/stats')
  return response.data
}

export async function getRecentCorrections(category?: string, limit?: number): Promise<Array<{
  category: string
  context: string | null
  original_value: string | null
  corrected_value: string
  explanation: string | null
  prompt_text: string
}>> {
  const params: Record<string, string | number> = {}
  if (category) params.category = category
  if (limit) params.limit = limit
  const response = await api.get('/corrections/recent', { params })
  return response.data
}

// Pricing tier for quantity-based margin pricing
export interface PricingTier {
  name: string
  min_quantity: number | null  // null means not configured
  max_quantity: number | null  // null means unlimited
  margin_percent: number | null  // The actual margin to apply for this tier
}

// Default pricing tiers (blank - to be configured per customer)
export const DEFAULT_PRICING_TIERS: PricingTier[] = [
  { name: "Tier 1", min_quantity: null, max_quantity: null, margin_percent: null },
  { name: "Tier 2", min_quantity: null, max_quantity: null, margin_percent: null },
  { name: "Tier 3", min_quantity: null, max_quantity: null, margin_percent: null },
]

// Customer types
export interface Customer {
  id: number
  name: string
  code: string | null
  markup_percent: number
  discount_percent: number
  effective_markup: number
  pricing_tiers: PricingTier[]
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  address: string | null
  notes: string | null
  active: boolean
  created_at: string
  job_count: number
  jobboss_customer_code: string | null
  jobboss_gl_code: string | null
}

export interface CustomerCreate {
  name: string
  code?: string
  markup_percent?: number
  discount_percent?: number
  pricing_tiers?: PricingTier[]
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  address?: string
  notes?: string
  jobboss_customer_code?: string
  jobboss_gl_code?: string
}

export interface CustomerUpdate {
  name?: string
  code?: string
  markup_percent?: number
  discount_percent?: number
  pricing_tiers?: PricingTier[]
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  address?: string
  notes?: string
  active?: boolean
  jobboss_customer_code?: string
  jobboss_gl_code?: string
}

// Customer API functions
export async function getCustomers(activeOnly: boolean = true): Promise<Customer[]> {
  const response = await api.get('/customers/', { params: { active_only: activeOnly } })
  return response.data
}

export async function getCustomer(customerId: number): Promise<Customer> {
  const response = await api.get(`/customers/${customerId}`)
  return response.data
}

export async function createCustomer(customer: CustomerCreate): Promise<Customer> {
  const response = await api.post('/customers/', customer)
  return response.data
}

export async function updateCustomer(customerId: number, customer: CustomerUpdate): Promise<Customer> {
  const response = await api.put(`/customers/${customerId}`, customer)
  return response.data
}

export async function deleteCustomer(customerId: number): Promise<void> {
  await api.delete(`/customers/${customerId}`)
}

export async function setPrintJobCustomer(jobId: number, customerId: number | null): Promise<void> {
  await api.put(`/prints/${jobId}/customer`, null, { params: { customer_id: customerId } })
}

// Customer Formula types
export interface CustomerFormula {
  id: number
  customer_id: number
  customer_name: string | null
  process_id: number
  process_code: string | null
  process_name: string | null
  name: string | null
  base_rate: number | null
  rate_per_hour: number | null
  minutes_per_unit: number | null
  formula_expression: string | null
  material_factors: Record<string, number> | null
  thickness_factors: Record<string, number> | null
  size_factors: Record<string, number> | null
  setup_cost: number | null
  minimum_charge: number | null
  notes: string | null
  effective_date: string | null
  active: boolean
  default_base_rate: number | null
  default_rate_per_hour: number | null
  default_minutes_per_unit: number | null
  default_minimum_charge: number | null
}

export interface CustomerFormulaCreate {
  customer_id: number
  process_id: number
  name?: string
  base_rate?: number
  rate_per_hour?: number
  minutes_per_unit?: number
  formula_expression?: string
  material_factors?: Record<string, number>
  thickness_factors?: Record<string, number>
  size_factors?: Record<string, number>
  setup_cost?: number
  minimum_charge?: number
  notes?: string
  effective_date?: string
}

export interface CustomerFormulaUpdate {
  name?: string
  base_rate?: number
  rate_per_hour?: number
  minutes_per_unit?: number
  formula_expression?: string
  material_factors?: Record<string, number>
  thickness_factors?: Record<string, number>
  size_factors?: Record<string, number>
  setup_cost?: number
  minimum_charge?: number
  notes?: string
  effective_date?: string
  active?: boolean
}

export interface CustomerFormulaCompare {
  process_code: string
  process_name: string
  has_override: boolean
  customer_base_rate: number | null
  default_base_rate: number | null
  customer_rate_per_hour: number | null
  default_rate_per_hour: number | null
  customer_minutes_per_unit: number | null
  default_minutes_per_unit: number | null
  customer_minimum: number | null
  default_minimum: number | null
  customer_setup: number | null
  default_setup: number | null
}

export interface CustomerFormulaSummary {
  customer: {
    id: number
    name: string
    code: string | null
    markup_percent: number
    discount_percent: number
    effective_markup: number
  }
  formula_overrides: CustomerFormula[]
  override_count: number
}

// Customer Formula API functions
export async function listCustomerFormulas(
  customerId?: number,
  processId?: number,
  activeOnly: boolean = true
): Promise<CustomerFormula[]> {
  const params: Record<string, number | boolean> = { active_only: activeOnly }
  if (customerId) params.customer_id = customerId
  if (processId) params.process_id = processId
  const response = await api.get('/customer-formulas/', { params })
  return response.data
}

export async function getCustomerFormula(formulaId: number): Promise<CustomerFormula> {
  const response = await api.get(`/customer-formulas/${formulaId}`)
  return response.data
}

export async function createCustomerFormula(formula: CustomerFormulaCreate): Promise<CustomerFormula> {
  const response = await api.post('/customer-formulas/', formula)
  return response.data
}

export async function createCustomerFormulaByCode(
  customerId: number,
  processCode: string,
  baseRate?: number,
  minimumCharge?: number,
  setupCost?: number,
  notes?: string
): Promise<CustomerFormula> {
  const params: Record<string, number | string> = {
    customer_id: customerId,
    process_code: processCode
  }
  if (baseRate !== undefined) params.base_rate = baseRate
  if (minimumCharge !== undefined) params.minimum_charge = minimumCharge
  if (setupCost !== undefined) params.setup_cost = setupCost
  if (notes !== undefined) params.notes = notes
  const response = await api.post('/customer-formulas/by-code', null, { params })
  return response.data
}

export async function updateCustomerFormula(
  formulaId: number,
  formula: CustomerFormulaUpdate
): Promise<CustomerFormula> {
  const response = await api.put(`/customer-formulas/${formulaId}`, formula)
  return response.data
}

export async function deleteCustomerFormula(formulaId: number): Promise<void> {
  await api.delete(`/customer-formulas/${formulaId}`)
}

export async function getCustomerFormulaCompare(customerId: number): Promise<CustomerFormulaCompare[]> {
  const response = await api.get(`/customer-formulas/customer/${customerId}/compare`)
  return response.data
}

export async function getCustomerFormulaSummary(customerId: number): Promise<CustomerFormulaSummary> {
  const response = await api.get(`/customer-formulas/customer/${customerId}/summary`)
  return response.data
}

// Company Settings types
export interface CompanySettings {
  id: number
  company_name: string
  address_street: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  phone: string | null
  email: string | null
  website: string | null
  logo_url: string | null
  updated_at: string | null
}

export interface CompanySettingsUpdate {
  company_name?: string
  address_street?: string
  address_city?: string
  address_state?: string
  address_zip?: string
  phone?: string
  email?: string
  website?: string
  logo_url?: string
}

// Company Settings API functions
export async function getCompanySettings(): Promise<CompanySettings> {
  const response = await api.get('/company/settings')
  return response.data
}

export async function updateCompanySettings(data: CompanySettingsUpdate): Promise<CompanySettings> {
  const response = await api.put('/company/settings', data)
  return response.data
}

export async function uploadCompanyLogo(
  file: File,
  onProgress?: (percent: number) => void
): Promise<CompanySettings> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post('/company/logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percent)
      }
    },
  })
  return response.data
}

export async function deleteCompanyLogo(): Promise<CompanySettings> {
  const response = await api.delete('/company/logo')
  return response.data
}

export function getCompanyLogoUrl(filename: string): string {
  return `${API_URL}/company/logo/${filename}`
}

// Theme types
export interface ThemeColors {
  primary: string
  primary_hover: string
  primary_light: string
  secondary: string
  accent: string
  background: string
  surface: string
  sidebar_bg: string
  sidebar_border: string
  text_primary: string
  text_secondary: string
  text_on_primary: string
  border: string
  success: string
  warning: string
  error: string
  info: string
}

export interface Theme {
  id: number
  name: string
  description: string | null
  is_system: boolean
  is_active: boolean
  colors: ThemeColors
  custom_css: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ThemeCreate {
  name: string
  description?: string
  colors: ThemeColors
  custom_css?: string
}

export interface ThemeUpdate {
  name?: string
  description?: string
  colors?: ThemeColors
  custom_css?: string
}

// Theme API functions
export async function getThemes(): Promise<Theme[]> {
  const response = await api.get('/themes/')
  return response.data
}

export async function getActiveTheme(): Promise<Theme> {
  const response = await api.get('/themes/active')
  return response.data
}

export async function getTheme(themeId: number): Promise<Theme> {
  const response = await api.get(`/themes/${themeId}`)
  return response.data
}

export async function createTheme(theme: ThemeCreate): Promise<Theme> {
  const response = await api.post('/themes/', theme)
  return response.data
}

export async function updateTheme(themeId: number, theme: ThemeUpdate): Promise<Theme> {
  const response = await api.put(`/themes/${themeId}`, theme)
  return response.data
}

export async function deleteTheme(themeId: number): Promise<void> {
  await api.delete(`/themes/${themeId}`)
}

export async function activateTheme(themeId: number): Promise<Theme> {
  const response = await api.put(`/themes/${themeId}/activate`)
  return response.data
}

export async function resetThemesToDefaults(): Promise<Theme[]> {
  const response = await api.post('/themes/reset-to-defaults')
  return response.data
}

// =====================
// JobBoss Estimate Sync
// =====================

export interface JobBossEstimateValidation {
  valid: boolean
  errors: string[]
  warnings: string[]
  formatted_part_number: string | null
}

export interface JobBossEstimatePreview {
  validation: JobBossEstimateValidation
  payload: Record<string, unknown> | null
}

export interface JobBossEstimatePushResult {
  success: boolean
  part_number: string | null
  error: string | null
  data: Record<string, unknown> | null
}

export interface JobBossAssemblyPushResult {
  success: boolean
  assembly_part_number: string | null
  subparts_created: string[]
  subparts_skipped: string[]
  subparts_failed: Array<{ part_number: string; error: string }>
  error: string | null
}

export interface JobBossExistsCheck {
  exists: boolean
  data: Record<string, unknown> | null
}

export async function formatJobBossPartNumber(
  partNumber: string,
  revision?: string | null
): Promise<{ formatted_part_number: string }> {
  const response = await api.post('/jobboss/estimates/format-pn', {
    part_number: partNumber,
    revision: revision || null
  })
  return response.data
}

export async function checkJobBossPartExists(
  formattedPartNumber: string
): Promise<JobBossExistsCheck> {
  const response = await api.get(`/jobboss/estimates/check/${encodeURIComponent(formattedPartNumber)}`)
  return response.data
}

export async function validateJobBossEstimate(
  printJobId: number
): Promise<JobBossEstimateValidation> {
  const response = await api.get(`/jobboss/estimates/validate/${printJobId}`)
  return response.data
}

export async function previewJobBossEstimate(
  printJobId: number
): Promise<JobBossEstimatePreview> {
  const response = await api.get(`/jobboss/estimates/preview/${printJobId}`)
  return response.data
}

export async function pushJobBossEstimate(
  printJobId: number
): Promise<JobBossEstimatePushResult> {
  const response = await api.post(`/jobboss/estimates/push/${printJobId}`)
  return response.data
}

export async function resetAssemblySync(assemblyId: number): Promise<{ message: string; reset_count: number }> {
  const response = await api.post(`/jobboss/estimates/reset-assembly-sync/${assemblyId}`)
  return response.data
}

export interface AssemblyPushProgress {
  current: number
  total: number
  part_number: string | null
}

export async function pushJobBossAssembly(
  assemblyId: number,
  onProgress?: (progress: AssemblyPushProgress) => void
): Promise<JobBossAssemblyPushResult> {
  // Start background task
  const startResponse = await api.post(`/jobboss/estimates/push-assembly/${assemblyId}`)
  const taskId = startResponse.data.task_id

  // Poll for completion
  const maxWait = 600_000 // 10 minutes
  const pollInterval = 2_000 // 2 seconds
  const startTime = Date.now()

  while (Date.now() - startTime < maxWait) {
    await new Promise(resolve => setTimeout(resolve, pollInterval))
    const statusResponse = await api.get(`/jobboss/estimates/push-assembly-status/${taskId}`)
    const status = statusResponse.data

    if (status.status === 'running' && status.progress && onProgress) {
      onProgress(status.progress as AssemblyPushProgress)
    }
    if (status.status === 'complete') {
      return status.result as JobBossAssemblyPushResult
    } else if (status.status === 'failed') {
      return {
        success: false,
        assembly_part_number: null,
        subparts_created: [],
        subparts_skipped: [],
        subparts_failed: [],
        error: status.error || 'Background task failed',
      }
    }
    // pending/running — keep polling
  }

  return {
    success: false,
    assembly_part_number: null,
    subparts_created: [],
    subparts_skipped: [],
    subparts_failed: [],
    error: 'Assembly push timed out after 10 minutes',
  }
}

export async function getJobBossStatus(): Promise<{
  enabled: boolean
  configured: boolean
  connected: boolean
  company_code: string | null
  company_name: string | null
  error: string | null
}> {
  const response = await api.get('/jobboss/status')
  return response.data
}

export async function setJobBossEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
  const response = await api.put('/jobboss/enabled', { enabled })
  return response.data
}

// Accuracy API functions
export async function getPrintAccuracy(printJobId: number): Promise<{
  print_job_id: number
  accuracy_score: number | null
  breakdown: Record<string, { score: number; weight: number; extracted: unknown; ground_truth: unknown }> | null
  is_ground_truth: boolean
  has_ground_truth: boolean
}> {
  const response = await api.get(`/accuracy/print/${printJobId}`)
  return response.data
}

export async function getRollingAccuracy(days: number = 30): Promise<{
  period_days: number
  total_scored: number
  average_accuracy: number | null
  by_field: Record<string, number>
}> {
  const response = await api.get('/accuracy/rolling', { params: { days } })
  return response.data
}

// Accuracy by field
export async function getAccuracyByField(): Promise<{
  total_scored: number
  overall_accuracy: number | null
  fields: Array<{ field: string; accuracy: number }>
}> {
  const response = await api.get('/accuracy/by-field')
  return response.data
}

// Recent accuracy
export async function getRecentAccuracy(limit: number = 20): Promise<{
  prints: Array<{
    print_job_id: number
    filename: string
    accuracy_score: number
    reviewed_at: string | null
    breakdown: Record<string, number> | null
  }>
  count: number
}> {
  const response = await api.get('/accuracy/recent', { params: { limit } })
  return response.data
}

// Holdout
export async function toggleHoldout(jobId: number): Promise<{ job_id: number; is_holdout: boolean }> {
  const response = await api.post(`/accuracy/holdout/${jobId}`)
  return response.data
}

// Star (protect from auto-cleanup)
export async function toggleStar(jobId: number): Promise<{ job_id: number; starred: boolean }> {
  const response = await api.post(`/prints/${jobId}/star`)
  return response.data
}

export async function getHoldoutPrints(): Promise<{
  holdout_prints: Array<{
    job_id: number
    filename: string
    status: string
    accuracy_score: number | null
    is_ground_truth: boolean
    has_ground_truth: boolean
  }>
  count: number
}> {
  const response = await api.get('/accuracy/holdout')
  return response.data
}

// Alerts
export interface AccuracyAlertItem {
  id: number
  alert_type: string
  severity: string
  message: string
  details: Record<string, unknown> | null
  print_job_id: number | null
  acknowledged: boolean
  created_at: string | null
}

export async function getAccuracyAlerts(acknowledged: boolean = false): Promise<{
  alerts: AccuracyAlertItem[]
  count: number
}> {
  const response = await api.get('/accuracy/alerts', { params: { acknowledged } })
  return response.data
}

export async function acknowledgeAlert(alertId: number): Promise<{ id: number; acknowledged: boolean }> {
  const response = await api.post(`/accuracy/alerts/${alertId}/acknowledge`)
  return response.data
}

export async function getAlertCount(): Promise<{ count: number }> {
  const response = await api.get('/accuracy/alerts/count')
  return response.data
}

// Training curator
export async function getCorrectionExamples(): Promise<{
  examples: Array<{
    id: number
    error_type: string
    process_code: string
    original_value: unknown
    corrected_value: unknown
    material_type: string | null
    gauge: number | null
    effectiveness_rate: number | null
    times_shown: number
    times_effective: number
    active: boolean
  }>
}> {
  const response = await api.get('/training/curator/correction-examples')
  return response.data
}

export async function getFormatRules(): Promise<{
  rules: Array<{
    id: number
    category: string
    rule_text: string
    source_print_id: number | null
    active: boolean
    token_count: number
  }>
}> {
  const response = await api.get('/training/curator/format-rules')
  return response.data
}

export async function getTokenBudget(): Promise<{
  format_rules: { current_tokens: number; budget: number; remaining: number; rule_count: number }
  correction_examples: { current_tokens: number; budget: number; remaining: number; example_count: number }
  budgets: Record<string, number>
}> {
  const response = await api.get('/training/curator/token-budget')
  return response.data
}

// === Approval Workflow ===

export interface ApprovalStage {
  id: number
  name: string
  order: number
  required_role: string | null
  auto_advance: boolean
  accuracy_threshold: number | null
  active: boolean
}

export interface ApprovalStatus {
  job_id: number
  current_stage: ApprovalStage | null
  next_stage: ApprovalStage | null
  is_final_stage: boolean
  history: Array<{ stage: string; action: string; user: string; timestamp: string; notes?: string }>
  stages_configured: boolean
}

export async function getApprovalStages(): Promise<ApprovalStage[]> {
  const response = await api.get('/approvals/stages')
  return response.data
}

export async function createApprovalStage(stage: Omit<ApprovalStage, 'id'>): Promise<ApprovalStage> {
  const response = await api.post('/approvals/stages', stage)
  return response.data
}

export async function updateApprovalStage(stageId: number, stage: Omit<ApprovalStage, 'id'>): Promise<ApprovalStage> {
  const response = await api.put(`/approvals/stages/${stageId}`, stage)
  return response.data
}

export async function deleteApprovalStage(stageId: number): Promise<void> {
  await api.delete(`/approvals/stages/${stageId}`)
}

export async function getApprovalStatus(jobId: number): Promise<ApprovalStatus> {
  const response = await api.get(`/approvals/${jobId}/status`)
  return response.data
}

export async function advanceApproval(jobId: number, notes?: string, user?: string): Promise<{ message: string; current_stage: string | null; is_final: boolean; fully_approved?: boolean }> {
  const response = await api.post(`/approvals/${jobId}/advance`, { notes, user })
  return response.data
}

export async function rejectApproval(jobId: number, notes: string, user?: string): Promise<{ message: string }> {
  const response = await api.post(`/approvals/${jobId}/reject`, { notes, user })
  return response.data
}

// === Nesting ===

export interface NestingResult {
  sheets_needed: number
  parts_per_sheet: number
  utilization_percent: number
  waste_percent: number
  sheet_width: number
  sheet_height: number
  kerf: number
  edge_boundary: number
  material_cost_per_sheet: number
  total_material_cost: number
  material_cost_per_part: number
  nest_svg: string
  geometry_source: 'dxf' | 'bounding_box'
  total_parts_placed: number
}

export async function getNestingPreview(jobId: number, quantity: number): Promise<NestingResult> {
  const response = await api.get(`/estimates/print/${jobId}/nesting`, { params: { quantity } })
  return response.data
}

// === Nesting Settings ===

export async function getNestingSettings(): Promise<Record<string, string>> {
  const response = await api.get('/materials/nesting-settings')
  return response.data
}

export async function updateNestingSettings(settings: Record<string, string>): Promise<Record<string, string>> {
  const response = await api.put('/materials/nesting-settings', settings)
  return response.data
}

// === Quote PDF ===

export async function generateQuotePDF(
  jobIds: number[],
  orderQuantities?: Record<number, number>,
  customerId?: number | null,
  notes?: string
): Promise<Blob> {
  const response = await api.post('/exports/quote-pdf', {
    job_ids: jobIds,
    customer_id: customerId || null,
    order_quantities: orderQuantities || {},
    notes: notes || ''
  }, { responseType: 'blob' })
  return response.data
}

// === RFQ ===

export interface RFQItem {
  id: number
  rfq_id: number
  print_job_id: number | null
  quantity: number
  unit_cost: number | null
  line_total: number | null
  notes: string | null
  filename?: string
  part_number?: string
  is_weldment?: boolean
  /** PrintJob status — in-flight statuses (pending/processing/converted/extracting) mean "still analyzing" */
  job_status?: string | null
}

export interface RFQ {
  id: number
  rfq_number: string
  name: string | null
  customer_id: number | null
  customer_name?: string
  status: string
  notes: string | null
  created_at: string
  updated_at: string | null
  items: RFQItem[]
  item_count?: number
  total_cost?: number
}

export async function listRFQs(): Promise<RFQ[]> {
  const response = await api.get('/rfq')
  return response.data
}

export async function getRFQ(rfqId: number): Promise<RFQ> {
  const response = await api.get(`/rfq/${rfqId}`)
  return response.data
}

export async function createRFQ(data: { name?: string; customer_id?: number; notes?: string }): Promise<RFQ> {
  const response = await api.post('/rfq', data)
  return response.data
}

export async function updateRFQ(rfqId: number, data: { name?: string; customer_id?: number; notes?: string; status?: string }): Promise<RFQ> {
  const response = await api.put(`/rfq/${rfqId}`, data)
  return response.data
}

export async function addRFQItems(rfqId: number, items: Array<{ print_job_id: number; quantity?: number }>): Promise<RFQItem[]> {
  const response = await api.post(`/rfq/${rfqId}/items`, items)
  return response.data
}

export async function updateRFQItem(rfqId: number, itemId: number, data: { quantity?: number }): Promise<RFQItem> {
  const response = await api.put(`/rfq/${rfqId}/items/${itemId}`, data)
  return response.data
}

export async function deleteRFQItem(rfqId: number, itemId: number): Promise<void> {
  await api.delete(`/rfq/${rfqId}/items/${itemId}`)
}

export async function costRFQ(rfqId: number): Promise<RFQ> {
  const response = await api.post(`/rfq/${rfqId}/cost`)
  return response.data
}

export async function generateRFQQuotePDF(rfqId: number): Promise<Blob> {
  const response = await api.post(`/rfq/${rfqId}/quote-pdf`, {}, { responseType: 'blob' })
  return response.data
}

// === RFQ timeline (events, notes, attachments) ===

export interface RFQEvent {
  id: number
  rfq_id: number
  event_type: string
  user_id: number | null
  username: string | null
  details: Record<string, unknown> | null
  created_at: string | null
}

export async function getRFQEvents(
  rfqId: number,
  page: number = 1,
  pageSize: number = 50
): Promise<{ items: RFQEvent[]; total: number; page: number; page_size: number }> {
  const response = await api.get(`/rfq/${rfqId}/events`, { params: { page, page_size: pageSize } })
  return response.data
}

export async function addRFQNote(
  rfqId: number,
  text: string,
  files: File[] = []
): Promise<{ rfq_id: number; note: string; files: Array<{ stored: string; original: string }> }> {
  const formData = new FormData()
  formData.append('text', text)
  for (const f of files) formData.append('files', f)
  const response = await api.post(`/rfq/${rfqId}/notes`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/** Download a note attachment (auth header required, so fetch as blob). */
export async function downloadRFQNoteFile(rfqId: number, storedName: string): Promise<Blob> {
  const response = await api.get(`/rfq/${rfqId}/notes/files/${storedName}`, { responseType: 'blob' })
  return response.data
}

// === Audit Log ===

export interface AuditLogEntry {
  id: number
  timestamp: string
  user_id: number | null
  username: string
  action: string
  entity_type: string
  entity_id: number
  details: Record<string, unknown> | null
}

export async function getAuditLogs(params?: {
  entity_type?: string
  action?: string
  user_id?: number
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
}): Promise<{ items: AuditLogEntry[]; total: number; page: number; page_size: number }> {
  const response = await api.get('/audit', { params })
  return response.data
}

// === ERP Connectors ===

export async function getERPConnectors(): Promise<{ connectors: Array<{ name: string; display_name: string; is_configured: boolean; is_active: boolean }> }> {
  const response = await api.get('/jobboss/erp/connectors')
  return response.data
}

// ----- Support tickets (Help button) -----

export interface SupportTicketSubmission {
  description: string
  url: string
  page_state: Record<string, unknown>
  console_errors: Array<{ message: string; stack: string; timestamp: number }>
  browser_info: Record<string, unknown>
}

export async function submitSupportTicket(body: SupportTicketSubmission): Promise<{ id: number; created_at: string }> {
  const resp = await api.post('/support/tickets', body)
  return resp.data
}
